import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { imageService, wetlandService } from '../services/apiService';
import {
  WetlandType,
  WaterClarity,
  WaterColor,
  CanSeeBottom,
  SurfaceAlgae,
  WaterPlants,
  SurroundingPlants,
  SeenAnimals,
  ObservedThreat,
} from '../constants/enums';
import CameraCapture from './CameraCapture';
import './WetlandForm.css';

const WetlandForm = ({ onClose, onSuccess, initialLocation }) => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [photoType, setPhotoType] = useState(null); // 'wetlandType' or 'threat'

  // Form state
  const [formData, setFormData] = useState({
    wetlandType: '',
    waterClarity: '',
    waterColor: '',
    canSeeBottom: '',
    surfaceAlgae: '',
    waterPlants: '',
    surroundingPlants: '',
    animalsInOrOnWater: '',
    seenAnimalsList: [],
    otherSeenAnimals: '',
    observedThreatsList: [],
    otherThreatDescription: '',
    location: null,
  });

  const [wetlandTypePhotos, setWetlandTypePhotos] = useState([]);
  const [wetlandTypePhotoUrls, setWetlandTypePhotoUrls] = useState([]);
  const [observedThreatPhotos, setObservedThreatPhotos] = useState([]);
  const [observedThreatPhotoUrls, setObservedThreatPhotoUrls] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const wetlandGalleryInputRef = useRef(null);
  const wetlandCameraInputRef = useRef(null);
  const threatGalleryInputRef = useRef(null);
  const threatCameraInputRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Only check user agent - this cannot be spoofed by resizing browser window
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      return isMobileDevice;
    };
    setIsMobile(checkMobile());
  }, []);

  useEffect(() => {
    if (initialLocation) {
      setFormData(prev => ({
        ...prev,
        location: {
          latitude: initialLocation.lat,
          longitude: initialLocation.lng,
        },
      }));
    }
  }, [initialLocation]);

  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
          let alertMessage = t('errorLocation2');
          if (error.code === 1) { 
            alertMessage += "\n\nMolimo vas da u podešavanjima pregledača omogućite pristup lokaciji za ovu stranicu.";
          }
          if (window.location.protocol !== 'https:') {
            alertMessage += "\n\nFunkcija geolokacije često zahteva sigurnu (https) konekciju.";
          }
          alert(alertMessage);
        }
      );
    } else {
      alert(t('errorLocationNotSupported'));
    }
  };

  const handlePhotoUpload = async (files, type, inputRef) => {
    if (!files || files.length === 0) {
      if (inputRef && inputRef.current) {
        inputRef.current.value = null;
      }
      return;
    }
    await processAndUploadFiles(Array.from(files), type);
    
    if (inputRef && inputRef.current) {
      inputRef.current.value = null;
      inputRef.current.type = 'text';
      inputRef.current.type = 'file';
    }
  };

  const openFilePicker = (inputRef) => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = null;
      inputRef.current.blur();
      
      setTimeout(() => {
        inputRef.current.click();
      }, 50);
    }
  };

  const handleCapture = async (file) => {
    if (!file) return;
    await processAndUploadFiles([file], photoType);
  };

  const processAndUploadFiles = (files, type) => {
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          alert(t('errorPhotoType'));
          continue;
        }
        if (file.size > 20 * 1024 * 1024) {
          alert(`${file.name} ${t('errorPhotoSize')}`);
          continue;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          if (type === 'wetlandType') {
            setWetlandTypePhotos((prev) => [...prev, { file, preview: e.target.result }]);
          } else {
            setObservedThreatPhotos((prev) => [...prev, { file, preview: e.target.result }]);
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Error processing files:', error);
      alert(t('errorPhoto'));
    }
  };

  const removePhoto = (index, type) => {
    if (type === 'wetlandType') {
      setWetlandTypePhotos((prev) => prev.filter((_, i) => i !== index));
      setWetlandTypePhotoUrls((prev) => prev.filter((_, i) => i !== index));
    } else {
      setObservedThreatPhotos((prev) => prev.filter((_, i) => i !== index));
      setObservedThreatPhotoUrls((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => {
      const currentList = prev[field] || [];
      const isSelected = currentList.includes(value);
      return {
        ...prev,
        [field]: isSelected
          ? currentList.filter((item) => item !== value)
          : [...currentList, value],
      };
    });
  };

  const handleCameraClick = (type) => {
    if (isMobile) {
      if (type === 'wetlandType') {
        wetlandCameraInputRef.current?.click();
      } else {
        threatCameraInputRef.current?.click();
      }
    } else {
      setPhotoType(type);
      setShowCamera(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.wetlandType) {
      setError(t('errorWetlandType'));
      return;
    }
    if (!formData.location) {
      setError(t('errorLocation'));
      return;
    }

    setSubmitting(true);

    try {
      const uploadedWetlandUrls = await Promise.all(
        wetlandTypePhotos.map(p => imageService.uploadImage(p.file))
      );
      const uploadedThreatUrls = await Promise.all(
        observedThreatPhotos.map(p => imageService.uploadImage(p.file))
      );

      const seenAnimalsValue =
      formData.seenAnimalsList && formData.seenAnimalsList.length > 0
        ? formData.seenAnimalsList[0]
        : null;

      const wetlandData = {
        observationTimestamp: new Date().toISOString(),
        wetlandType: formData.wetlandType,
        wetlandTypePhotoUrls: uploadedWetlandUrls,
        waterClarity: formData.waterClarity || null,
        waterColor: formData.waterColor || null,
        canSeeBottom: formData.canSeeBottom || null,
        surfaceAlgae: formData.surfaceAlgae || null,
        waterPlants: formData.waterPlants || null,
        surroundingPlants: formData.surroundingPlants || null,
        animalsInOrOnWater: formData.animalsInOrOnWater || null,
        seenAnimalsList: formData.seenAnimalsList,
        otherSeenAnimals: formData.otherSeenAnimals || null,
        location: formData.location,
        observedThreatsList: formData.observedThreatsList,
        observedThreatPhotoUrls: uploadedThreatUrls,
        otherThreatDescription: formData.otherThreatDescription || null,
      };

      await wetlandService.createWetland(wetlandData);
      setSuccess(true);
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting wetland:', error);
      setError(t('errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

    return (
      <>
        <div className="wetland-form-modal" onClick={onClose}>
          <div className="wetland-form-container" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h2>{t('report')}</h2>
              <button className="close-button" onClick={onClose}>
                ×
              </button>
            </div>
  
            <form onSubmit={handleSubmit}>
              <div className="form-body">
                {success && (
                  <div className="success-message">
                    {t('successMessage')}
                  </div>
                )}
  
                {error && (
                  <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#ffe0e0', borderRadius: '8px' }}>
                    {error}
                  </div>
                )}
  
                {/* Water details section */}
                <div className="form-section">
                  <h3>{t('waterDetails')}</h3>
  
                  <div className="form-group">
                    <label className="required">{t('Tip vlažnog staništa')}</label>
                    <select
                      value={formData.wetlandType}
                      onChange={(e) => setFormData({ ...formData, wetlandType: e.target.value })}
                      required
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(WetlandType).map((type) => (
                        <option key={type} value={type}>
                          {t(type)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('waterClarity')}</label>
                    <select
                      value={formData.waterClarity}
                      onChange={(e) => setFormData({ ...formData, waterClarity: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(WaterClarity).map((clarity) => (
                        <option key={clarity} value={clarity}>
                          {t(clarity)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('waterColor')}</label>
                    <select
                      value={formData.waterColor}
                      onChange={(e) => setFormData({ ...formData, waterColor: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(WaterColor).map((color) => (
                        <option key={color} value={color}>
                          {t(color)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('canSeeBottom')}</label>
                    <select
                      value={formData.canSeeBottom}
                      onChange={(e) => setFormData({ ...formData, canSeeBottom: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(CanSeeBottom).map((option) => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('surfaceAlgae')}</label>
                    <select
                      value={formData.surfaceAlgae}
                      onChange={(e) => setFormData({ ...formData, surfaceAlgae: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(SurfaceAlgae).map((option) => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('waterPlants')}</label>
                    <select
                      value={formData.waterPlants}
                      onChange={(e) => setFormData({ ...formData, waterPlants: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(WaterPlants).map((option) => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('surroundingPlants')}</label>
                    <select
                      value={formData.surroundingPlants}
                      onChange={(e) => setFormData({ ...formData, surroundingPlants: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      {Object.values(SurroundingPlants).map((option) => (
                        <option key={option} value={option}>
                          {t(option)}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  {/* Wetland type photos */}
                  <div className="form-group">
                    <label>{t('Fotografije vlažnog staništa')}</label>
                    <div className="photo-upload-area">
                      {/* Gallery input - BEZ capture (za oba) */}
                      <input
                        ref={wetlandGalleryInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e.target.files, 'wetlandType', wetlandGalleryInputRef)}
                        style={{ display: 'none' }}
                      />
                      {/* Camera input - SA capture (samo za mobile) */}
                      <input
                        ref={wetlandCameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handlePhotoUpload(e.target.files, 'wetlandType', wetlandCameraInputRef)}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => wetlandGalleryInputRef.current?.click()}
                        disabled={uploadingPhotos}
                      >
                        🖼️ {uploadingPhotos ? t('uploading') : t('uploadPhoto')}
                      </button>
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => handleCameraClick('wetlandType')}
                        disabled={uploadingPhotos}
                      >
                        📸 {t('takePhoto')}
                      </button>
                    </div>
                    {wetlandTypePhotos.length > 0 && (
                      <div className="photo-preview-grid">
                        {wetlandTypePhotos.map((photo, index) => (
                          <div key={index} className="photo-preview-item">
                            <img src={photo.preview} alt={`Wetland ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-photo"
                              onClick={() => removePhoto(index, 'wetlandType')}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
  
                {/* Wildlife section */}
                <div className="form-section">
                  <h3>{t('wildlife')}</h3>
  
                  <div className="form-group">
                    <label>{t('animalsInOrOnWater')}</label>
                    <select
                      value={formData.animalsInOrOnWater}
                      onChange={(e) => setFormData({ ...formData, animalsInOrOnWater: e.target.value })}
                    >
                      <option value="">{t('selectOption')}</option>
                      <option value="YES">{t('YES')}</option>
                      <option value="NO">{t('NO')}</option>
                    </select>
                  </div>
  
                  <div className="form-group">
                    <label>{t('seenAnimals')}</label>
                    <div className="checkbox-group">
                      {Object.values(SeenAnimals).map((animal) => (
                        <div
                          key={animal}
                          className={`checkbox-item ${
                            formData.seenAnimalsList.includes(animal) ? 'selected' : ''
                          }`}
                          onClick={() => handleCheckboxChange('seenAnimalsList', animal)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.seenAnimalsList.includes(animal)}
                            onChange={() => {}}
                          />
                          <label>{t(animal)}</label>
                        </div>
                      ))}
                    </div>
                  </div>
  
                  {formData.seenAnimalsList.includes('OTHER') && (
                    <div className="form-group">
                      <label>{t('otherSeenAnimals')}</label>
                      <textarea
                        value={formData.otherSeenAnimals}
                        onChange={(e) => setFormData({ ...formData, otherSeenAnimals: e.target.value })}
                        placeholder={t('describeAnimals')}
                      />
                    </div>
                  )}
                </div>
  
                {/* Threats section */}
                <div className="form-section">
                  <h3>{t('observedThreats')}</h3>
  
                  <div className="form-group">
                    <label>Koje opasnosti ste uočili</label>
                    <div className="checkbox-group">
                      {Object.values(ObservedThreat).map((threat) => (
                        <div
                          key={threat}
                          className={`checkbox-item ${
                            formData.observedThreatsList.includes(threat) ? 'selected' : ''
                          }`}
                          onClick={() => handleCheckboxChange('observedThreatsList', threat)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.observedThreatsList.includes(threat)}
                            onChange={() => {}}
                          />
                          <label>{t(threat)}</label>
                        </div>
                      ))}
                    </div>
                  </div>
  
                  {formData.observedThreatsList.includes('OTHER') && (
                    <div className="form-group">
                      <label>{t('otherThreatDescription')}</label>
                      <textarea
                        value={formData.otherThreatDescription}
                        onChange={(e) => setFormData({ ...formData, otherThreatDescription: e.target.value })}
                        placeholder={t('describeThreats')}
                      />
                    </div>
                  )}
  
                  {/* Threat photos */}
                  {formData.observedThreatsList.length > 0 && (
                    <div className="form-group">
                      <label>{t('observedThreatPhotos')}</label>
                      <div className="photo-upload-area">
                        {/* Gallery input - BEZ capture */}
                        <input
                          ref={threatGalleryInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handlePhotoUpload(e.target.files, 'threat', threatGalleryInputRef)}
                          style={{ display: 'none' }}
                        />
                        {/* Camera input - SA capture (samo za mobile) */}
                        <input
                          ref={threatCameraInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhotoUpload(e.target.files, 'threat', threatCameraInputRef)}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="upload-button"
                          onClick={() => threatGalleryInputRef.current?.click()}
                          disabled={uploadingPhotos}
                        >
                          🖼️ {uploadingPhotos ? t('uploading') : t('uploadPhoto')}
                        </button>
                        <button
                          type="button"
                          className="upload-button"
                          onClick={() => handleCameraClick('threat')}
                          disabled={uploadingPhotos}
                        >
                          📸 {t('takePhoto')}
                        </button>
                      </div>
                      {observedThreatPhotos.length > 0 && (
                        <div className="photo-preview-grid">
                          {observedThreatPhotos.map((photo, index) => (
                            <div key={index} className="photo-preview-item">
                              <img src={photo.preview} alt={`Threat ${index + 1}`} />
                              <button
                                type="button"
                                className="remove-photo"
                                onClick={() => removePhoto(index, 'threat')}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
  
                {/* Location section */}
                <div className="form-section">
                  <h3>{t('location')}</h3>
  
                  <div className="form-group">
                    <button
                      type="button"
                      className="location-button"
                      onClick={getCurrentLocation}
                    >
                      📍 {t('getCurrentLocation')}
                    </button>
                    {formData.location && (
                      <div className="location-display">
                        Lat: {formData.location.latitude.toFixed(6)}, Lng:{' '}
                        {formData.location.longitude.toFixed(6)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
  
              <div className="form-footer">
                <button type="button" className="cancel-button" onClick={onClose}>
                  {t('cancel')}
                </button>
                {isMobile && (
                  <button type="submit" className="submit-button" disabled={submitting || uploadingPhotos}>
                    {submitting ? t('submitting') : t('submit')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        {showCamera && (
          <CameraCapture
            onCapture={handleCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </>
    );
  };
export default WetlandForm;